'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { authenticate } from '@/lib/auth';
import { useAuth } from '@/lib/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setUser } = useAuth();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 800));

    const user = authenticate(data.email, data.password);

    if (!user) {
      setIsLoading(false);
      setError('root', {
        message: 'Invalid email or password. Please check your credentials.',
      });
      return;
    }

    setUser(user);

    toast.success(`Welcome back, ${user.name}`, {
      description: user.role === 'admin' ? 'Admin access granted — full edit control enabled.' : 'Redirecting to Risk Dashboard…',
    });

    await new Promise(r => setTimeout(r, 800));
    window.location.href = '/ai-risk-dashboard';
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <div>
        <h1 className="text-xl font-semibold text-foreground mb-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground">Sign in to your GlobalAtlas account</p>
      </div>

      {/* Root error */}
      {errors.root && (
        <div className="bg-danger/10 border border-danger/20 rounded-lg px-4 py-3">
          <p className="text-xs text-danger">{errors.root.message}</p>
        </div>
      )}

      {/* Admin notice */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 flex items-start gap-2">
        <Shield size={13} className="text-primary mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Platform owner? Sign in with <span className="text-primary font-medium">admin@globalatlas.io</span> for full edit access.
        </p>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="login-email" className="block text-xs font-medium text-foreground mb-1.5">
          Email Address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          className={`w-full bg-input border rounded-md px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${
            errors.email ? 'border-danger' : 'border-border'
          }`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="login-password" className="block text-xs font-medium text-foreground">
            Password
          </label>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Enter your password"
            className={`w-full bg-input border rounded-md px-3 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${
              errors.password ? 'border-danger' : 'border-border'
            }`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          id="remember-me"
          type="checkbox"
          className="w-3.5 h-3.5 accent-primary rounded"
          {...register('rememberMe')}
        />
        <label htmlFor="remember-me" className="text-xs text-muted-foreground cursor-pointer">
          Keep me signed in
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-md hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
        style={{ minHeight: '42px' }}
      >
        {isLoading ? (
          <>
            <Loader2 size={15} className="animate-spin" />
            <span>Signing in…</span>
          </>
        ) : (
          'Sign In'
        )}
      </button>

      <p className="text-center text-xs text-muted-foreground">
        Don&apos;t have an account?{' '}
        <button type="button" onClick={onSwitchToSignup} className="text-primary hover:text-primary/80 font-medium transition-colors">
          Create account
        </button>
      </p>
    </form>
  );
}